import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { CompareProvider } from "@/lib/compare";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ElfsightWidget from "@/components/ElfsightWidget";
import CompareTray from "@/components/CompareTray";

import HomePage from "@/pages/HomePage";
import CarsPage from "@/pages/CarsPage";
import CarDetailPage from "@/pages/CarDetailPage";
import ComparePage from "@/pages/ComparePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ContactPage from "@/pages/ContactPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ImportRequestPage from "@/pages/ImportRequestPage";
import DetailingPage from "@/pages/DetailingPage";
import DiagnosticsPage from "@/pages/DiagnosticsPage";
import BookingPage from "@/pages/BookingPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminLayout, { AdminDashboard } from "@/pages/AdminLayout";
import AdminCarsPage from "@/pages/AdminCarsPage";
import AdminMessagesPage from "@/pages/AdminMessagesPage";
import AdminTestimonialsPage from "@/pages/AdminTestimonialsPage";
import { AdminFinancePage, AdminTradeInPage } from "@/pages/AdminRequestsPages";
import AdminCalendarPage from "@/pages/AdminCalendarPage";

function ScrollHandler() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
                return;
            }
        }
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

function PublicLayout({ children }) {
    return (
        <>
            {/* Global KS logo watermark — sits behind every public section that has a transparent bg */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 -z-10 flex items-start justify-center opacity-[0.08] select-none"
                style={{
                    backgroundImage: "url(/ks-logo.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center 25vh",
                    backgroundSize: "min(65vw, 1000px) auto",
                    backgroundAttachment: "fixed",
                }}
            />
            <Navbar />
            {children}
            <Footer />
            <CompareTray />
            {/* Floating AI Chatbot — only on public pages */}
            <div data-testid="elfsight-chatbot">
                <ElfsightWidget appId="7c46d97b-0606-46b1-8c44-82517aa9f7f5" />
            </div>
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <CompareProvider>
                <BrowserRouter>
                    <ScrollHandler />
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <PublicLayout>
                                    <HomePage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/cars"
                            element={
                                <PublicLayout>
                                    <CarsPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/cars/:id"
                            element={
                                <PublicLayout>
                                    <CarDetailPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/compare"
                            element={
                                <PublicLayout>
                                    <ComparePage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/services/import"
                            element={
                                <PublicLayout>
                                    <ImportRequestPage />
                                </PublicLayout>
                            }
                        />
                        <Route
                            path="/services/detailing"
                            element={
                                <PublicLayout>
                                    <DetailingPage />
                                </PublicLayout>
                            }
                        />
                        <Route path="/services/diagnostics" element={<PublicLayout><DiagnosticsPage /></PublicLayout>} />
                        <Route path="/services/booking" element={<PublicLayout><BookingPage /></PublicLayout>} />
                        <Route path="/booking" element={<Navigate to="/services/booking" replace />} />

                        {/* SEO-friendly section pages */}
                        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                        <Route path="/services" element={<PublicLayout><ServicesPage /></PublicLayout>} />
                        <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                        <Route path="/reviews" element={<PublicLayout><ReviewsPage /></PublicLayout>} />

                        {/* Legacy WebNode / mobile.bg redirects → permanent SPA redirects */}
                        <Route path="/obyavi" element={<Navigate to="/cars" replace />} />
                        <Route path="/obyavi/*" element={<Navigate to="/cars" replace />} />
                        <Route path="/obiavi" element={<Navigate to="/cars" replace />} />
                        <Route path="/obiavi/*" element={<Navigate to="/cars" replace />} />
                        <Route path="/kontakt" element={<Navigate to="/contact" replace />} />
                        <Route path="/kontakt/*" element={<Navigate to="/contact" replace />} />
                        <Route path="/za-nas" element={<Navigate to="/about" replace />} />
                        <Route path="/za-nas/*" element={<Navigate to="/about" replace />} />
                        <Route path="/uslugi" element={<Navigate to="/services" replace />} />
                        <Route path="/uslugi/*" element={<Navigate to="/services" replace />} />
                        <Route path="/avtomobili" element={<Navigate to="/cars" replace />} />
                        <Route path="/avtomobili/*" element={<Navigate to="/cars" replace />} />
                        <Route path="/galeriya" element={<Navigate to="/" replace />} />
                        <Route path="/galeriya/*" element={<Navigate to="/" replace />} />
                        <Route path="/otzivi" element={<Navigate to="/reviews" replace />} />
                        <Route path="/nachalo" element={<Navigate to="/" replace />} />
                        <Route path="/home" element={<Navigate to="/" replace />} />
                        <Route path="/index.html" element={<Navigate to="/" replace />} />

                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<AdminDashboard />} />
                            <Route path="cars" element={<AdminCarsPage />} />
                            <Route path="messages" element={<AdminMessagesPage />} />
                            <Route path="testimonials" element={<AdminTestimonialsPage />} />
                            <Route path="finance" element={<AdminFinancePage />} />
                            <Route path="tradein" element={<AdminTradeInPage />} />
                            <Route path="calendar" element={<AdminCalendarPage />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </CompareProvider>
        </AuthProvider>
    );
}

export default App;
